import { ChangeEvent, useEffect } from 'react';

import { InputLabel, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';
import { MidYearReportRequest, MidYearReportSchema, YesNo } from '@youscience/commonapp-service-common';
import { CoreFormControl } from '@youscience/core';

import RadioButtonGroupWithClearButton from '@components/RadioGroupWithClearButton';
import SectionAccordion from '@components/SectionAccordion';

import { getFilename } from '../../../../common/formUtils';
import FileUpload from '../../../../common/FileUpload';

import { FormState, FormStateErrors } from '@constants/commonApp/types';
import { StyledFieldsContainer } from '@pages/RecommenderSpace/components/Card.styles';
import { useApplicantFormSection } from '@pages/StudentDetailsPage/hooks/useApplicantFormSection';
import { TabSections } from '@interfaces/recommenderWorkspace';

export type SummaryFields = Partial<MidYearReportRequest[TabSections.Summary]>;

interface SummaryProps {
  summary?: SummaryFields;
  onFormChange: (formState: FormState<SummaryFields>) => void;
  onExpand: () => void;
  onContinue?: () => void;
  isExtended: boolean;
  isSaved: boolean;
  applicantName?: string;
  showError?: boolean;
}

const validator = MidYearReportSchema.shape.summary;

const WORD_LIMIT = 250;

const countWords = (text: string) => (text.trim() ? text.trim().split(/\s+/).length : 0);

const validateSummary = (state: FormState<SummaryFields>): FormStateErrors<SummaryFields> => {
  if (state.isSaved && state.data.explanationType === '1' && countWords(state.data.writtenExplanation || '') < 1) {
    return { writtenExplanation: 'Please enter at least 1 word' } as FormStateErrors<SummaryFields>;
  }
  return {} as FormStateErrors<SummaryFields>;
};

const isWriteModeValid = (data: SummaryFields) =>
  (data.explanationType ?? '0') !== '1' || countWords(data.writtenExplanation || '') >= 1;

export const Summary = ({
  summary = {},
  onFormChange,
  onExpand,
  isExtended,
  isSaved,
  applicantName = '',
  showError = false,
}: SummaryProps) => {
  const handleFormChange = (fs: FormState<SummaryFields>) => {
    // Only ever tighten validity: keep the hook's schema-based result and additionally
    // require the write-mode word count. Never force it back to true.
    onFormChange({ ...fs, isFormValid: fs.isFormValid && isWriteModeValid(fs.data) });
  };

  const [formState, setFormStateWithErrors] = useApplicantFormSection<SummaryFields, typeof validator>(
    summary,
    validator,
    isSaved,
    handleFormChange,
    validateSummary,
  );

  const showResponseOptions =
    formState.data.courseChange === YesNo.Yes || formState.data.updateEvaluation === YesNo.Yes;

  const explanationType = formState.data.explanationType ?? '0';
  const isWriteMode = explanationType === '1';
  const writtenWordCount = countWords(formState.data.writtenExplanation || '');

  const isError = showError || !formState.isFormValid || (isWriteMode && writtenWordCount < 1);

  useEffect(() => {
    if (!showResponseOptions) {
      // Hidden response options: drop explanationType and any explanation the user entered,
      // so stale upload/written data isn't persisted or submitted.
      if (
        formState.data.explanationType != null ||
        formState.data.changeExplanation != null ||
        formState.data.writtenExplanation != null
      ) {
        setFormStateWithErrors({
          explanationType: undefined,
          changeExplanation: undefined,
          writtenExplanation: undefined,
        });
      }
    } else if (formState.data.explanationType == null) {
      // Seed the default so explanationType is persisted (the schema requires it when a
      // question is answered Yes), even if the user never touches the Select. '0' = Uploaded.
      setFormStateWithErrors({ explanationType: '0' });
    }
  }, [showResponseOptions]);

  const handleRadioChange = (field: string) => (event: ChangeEvent<HTMLInputElement>) => {
    setFormStateWithErrors({ [field]: event.target.value });
  };

  const handleFileChange = (field: string) => async (file: string | undefined) => {
    setFormStateWithErrors({ [field]: file });
  };

  const handleResponseMethodChange = (event: SelectChangeEvent<unknown>) => {
    setFormStateWithErrors({
      explanationType: event.target.value as SummaryFields['explanationType'],
      changeExplanation: undefined,
      writtenExplanation: undefined,
    });
  };

  const handleTextResponseChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (countWords(value) <= WORD_LIMIT) {
      setFormStateWithErrors({ writtenExplanation: value || undefined });
    }
  };

  return (
    <SectionAccordion
      title="Summary"
      isExpanded={isExtended}
      onChange={onExpand}
      isChanged={formState.hasChanged}
      showCheckmark={formState.isSaved && !isError}
      isError={isError}
    >
      <StyledFieldsContainer>
        <RadioButtonGroupWithClearButton
          title="Have there been any changes to the senior year courses sent with the original School Report?"
          name="courseChange"
          value={formState.data.courseChange ?? undefined}
          options={YesNo}
          required
          handleRadioChange={handleRadioChange}
          handleClear={() => setFormStateWithErrors({ courseChange: undefined })}
          error={!!formState.errors.courseChange}
          helperText={formState.errors.courseChange}
        />

        <RadioButtonGroupWithClearButton
          title="Do you wish to update your original evaluation of this applicant?"
          name="updateEvaluation"
          value={formState.data.updateEvaluation ?? undefined}
          options={YesNo}
          required
          handleRadioChange={handleRadioChange}
          handleClear={() => setFormStateWithErrors({ updateEvaluation: undefined })}
          error={!!formState.errors.updateEvaluation}
          helperText={formState.errors.updateEvaluation}
        />

        {showResponseOptions && (
          <>
            <CoreFormControl fullWidth>
              <InputLabel>How would you like to add your changes or updates?</InputLabel>
              <Select
                label="How would you like to add your changes or updates?"
                value={explanationType}
                onChange={handleResponseMethodChange}
              >
                <MenuItem value="0">Upload a document</MenuItem>
                <MenuItem value="1">Write a response</MenuItem>
              </Select>
            </CoreFormControl>

            {explanationType === '0' ? (
              <FileUpload
                title="Since you answered yes to one of the preceding questions, please attach an explanation."
                name="changeExplanation"
                initialFile={formState.data.changeExplanation}
                previewTitle="Current Courses Transcript"
                filename={
                  formState.data.changeExplanation ? getFilename(applicantName, 'change_explanation') : undefined
                }
                onFileUpload={handleFileChange}
                onFileDelete={handleFileChange}
                error={formState.errors.changeExplanation}
              />
            ) : (
              <TextField
                label="Changes or updates"
                multiline
                rows={4}
                fullWidth
                value={formState.data.writtenExplanation || ''}
                onChange={handleTextResponseChange}
                helperText={`${writtenWordCount} / ${WORD_LIMIT} words`}
                error={!!formState.errors.writtenExplanation}
              />
            )}
          </>
        )}
      </StyledFieldsContainer>
    </SectionAccordion>
  );
};
